import React, { useRef, useState, useEffect } from 'react'
import ApiGateway from 'theme/api/api-gateway'
import { useFirebaseService } from 'theme/services/firebase-service'
import { useAuthService } from 'theme/services/auth/auth-service'
import { getConferenceTickets } from 'theme/utils/auth-helpers'
import { Link } from 'gatsby'
import { getEventLink } from 'theme/utils/generic-helpers'

type Registration = {
  sessionId: string
  uid: string
  sessionType: string
  registration?: {
    timestamp: number
    status: string
  }
}

interface InPersonActivityRegisterButtonProps {
  conferenceId: string
  sessionId: string
  userRegistration: Registration | null
  sessionCapacity: number
  recordId: string
  conferenceLocation: string
  calendarId: string
  startTime: string
  sessionName: string
  sessionSlug: string
  sessionType: string
}

const InPersonActivityRegisterButton = ({
  conferenceId,
  conferenceLocation,
  sessionId,
  userRegistration,
  sessionCapacity,
  recordId,
  calendarId,
  startTime,
  sessionName,
  sessionSlug,
  sessionType,
}: InPersonActivityRegisterButtonProps) => {
  const { user } = useAuthService()
  const firebase = useFirebaseService()
  const freeRequestQueue = useRef(true)
  const [hasInPersonTicket, setHasInPersonTicket] = useState(false)
  const [tickets, setTickets] = useState(false)

  useEffect(() => {
    const { tickets, inPersonTickets } = getConferenceTickets(user, conferenceId)
    if (inPersonTickets.length > 0) setHasInPersonTicket(true)
    if (tickets.length > 0) setTickets(true)
  }, [user])

  const registerToInPersonActivity = async () => {
    freeRequestQueue.current = false

    const transactionCompleted = await ApiGateway.registerUserInPersonActivitySession(
      firebase,
      user.uid,
      conferenceId,
      sessionId,
      sessionCapacity,
      recordId,
      calendarId,
      startTime,
      sessionName,
      sessionSlug,
      sessionType,
    )

    if (transactionCompleted) {
      const registration = {
        ...userRegistration,
        sessionType,
        registration: {
          status: 'registered',
          timestamp: Date.now(),
        },
      }

      await ApiGateway.addUserRegistration(
        firebase,
        user.uid,
        conferenceId,
        sessionId,
        registration,
        sessionType,
        calendarId,
        startTime,
        sessionName,
        sessionSlug,
        recordId
      )
    }

    freeRequestQueue.current = true
  }

  const unregisterFromInPersonActivity = async () => {
    freeRequestQueue.current = false

    const transactionCompleted = await ApiGateway.unregisterUserInPersonActivitySession(
      firebase,
      user.uid,
      sessionId,
    )

    if (transactionCompleted) {
      await ApiGateway.removeUserRegistration(firebase, user.uid, conferenceId, sessionId)
    }

    freeRequestQueue.current = true
  }

  const isRegistered = () => {
    return (
      userRegistration?.sessionType === sessionType && userRegistration?.registration?.status === 'registered'
    )
  }

  return (
    <>
      {hasInPersonTicket && (
        <>
          {isRegistered() ? (
            <button
              onClick={() => unregisterFromInPersonActivity()}
              className="text-sm ml-4 py-1 px-2 rounded border-2 border-gray-400 text-gray-600 hover:bg-gray-200 hover:text-gray-800 focus:ring-2 focus:ring-gray-200 focus:bg-gray-200 focus:text-gray-500 active:bg-gray-200 active:text-gray-500 transition duration-150 ease-in-out"
            >
              Unregister from {sessionType.toLowerCase()}
            </button>
          ) : (
            <button
              onClick={() => registerToInPersonActivity()}
              className="text-sm ml-4 py-1 px-2 rounded border-2 border-orange-400 text-orange-600 hover:bg-orange-200 hover:text-orange-800 focus:ring-2 focus:ring-orange-100 focus:bg-orange-200 focus:text-orange-500 active:bg-orange-200 active:ring-2 active:ring-orange-100 active:text-orange-500 transition duration-150 ease-in-out"
            >
              Register for {sessionType.toLowerCase()}
            </button>
          )}
        </>
      )}
      {tickets && !hasInPersonTicket && (
        <>
          <Link
            to={getEventLink(conferenceId, conferenceLocation, 'tickets')}
            className="text-sm ml-4 py-1 px-2 rounded border-2 border-orange-400 text-orange-600 hover:bg-orange-200 hover:text-orange-800 focus:ring-2 focus:ring-orange-100 focus:bg-orange-200 focus:text-orange-500 active:bg-orange-200 active:ring-2 active:ring-orange-100 active:text-orange-500 transition duration-150 ease-in-out"
          >
            Upgrade to In-Person
          </Link>
        </>
      )}
      {/* {!tickets && (
        <>
          <div className="text-sm ml-4 py-1 px-2 text-orange-500 font-bold border-2 border-orange-500 rounded">
            In-Person Ticket Only
          </div>
        </>
      )} */}
    </>
  )
}

export default InPersonActivityRegisterButton
